import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import toast, { Toaster } from 'react-hot-toast';
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { ProjectProvider } from './contexts/ProjectContext';
import { WizardProvider } from './contexts/WizardContext';
import { QuickNavProvider } from './contexts/QuickNavContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TerminologyProvider } from './contexts/TerminologyContext';
import { AIContextProvider } from './contexts/AIContext';
import { HierarchyNavigationProvider } from './contexts/HierarchyNavigationContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Navigation } from './components/Navigation/Navigation';
import { AppHeader } from './components/AppHeader/AppHeader';
import { PageErrorBoundary } from './components/ErrorBoundary';
// QuickNavSidebar disabled - conflicts with left side navigation bar
// import { QuickNavSidebar } from './components/QuickNavSidebar/QuickNavSidebar';
import { CommandPalette } from './components/CommandPalette/CommandPalette';
import { KeyboardShortcutsHelp } from './components/KeyboardShortcutsHelp/KeyboardShortcutsHelp';
import { WizardShell } from './components/wizard/WizardShell';
import { MigrationWelcomeModal } from './components/migration/MigrationWelcomeModal';
import { TerminalChatPanel } from './components/ai-chat';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useFirstVisit } from './hooks/useFirstVisit';
import { useSettings } from './hooks/useSettings';
import { BuildBadge } from './components/BuildBadge';
import { InterlockScaffold } from './components/interlock/InterlockScaffold';
import './App.css';

// ============================================================================
// Lazy-loaded page components for code splitting
// This reduces initial bundle size and improves load time
// ============================================================================

// Interlock pivot — trade record (design 13a), superseded by TradeDetailPage (P1/F2).
// Kept in the tree but unrouted; remove once the v2 detail is proven.
const InterlockTradeRecordPage = lazy(() => import('./pages/interlock/TradeRecordPage'));
// Interlock Trade Detail (P1 / F2) — the hero screen: read zone + commit-graph + live composer.
const InterlockTradeDetailPage = lazy(() => import('./pages/interlock/TradeDetailPage'));
// Interlock shell (P1) — ICD Register / Matrix / System View / Waiting-on-me home.
// Each page renders its own nav rail; no legacy FMEA Studio chrome.
const InterlockHomePage = lazy(() => import('./pages/interlock/HomePage'));
const InterlockRegisterPage = lazy(() => import('./pages/interlock/InterfaceRegisterPage'));
const InterlockMatrixPage = lazy(() => import('./pages/interlock/InterfaceMatrixPage'));
const InterlockSystemViewPage = lazy(() => import('./pages/interlock/SystemViewPage'));
const InterlockGatePage = lazy(() => import('./pages/interlock/GatePage'));
// Parcel P2 — project-status landing page (composes gate/coverage/risk/FMEA).
const InterlockStatusPage = lazy(() => import('./pages/interlock/StatusPage'));
const InterlockWorkloadPage = lazy(() => import('./pages/interlock/WorkloadPage'));
const InterlockCampaignsPage = lazy(() => import('./pages/interlock/CampaignsPage'));
const InterlockCampaignDetailPage = lazy(() => import('./pages/interlock/CampaignDetailPage'));
const InterlockInterfaceDetail = lazy(() => import('./pages/interlock/InterfaceDetailPage'));
// Ontology O4 — component page v1 (identity, children, interfaces, decisions).
const InterlockComponentPage = lazy(() => import('./pages/interlock/ComponentPage'));
// Component Spec-Sheet v1 — Components nav list + per-component spec sheet
// (interface-obligations rollup from crossings + functions/FMEA/reqs + attributes).
const InterlockComponentsListPage = lazy(() => import('./pages/interlock/ComponentsListPage'));
const InterlockComponentSpecPage = lazy(() => import('./pages/interlock/ComponentSpecPage'));
// Campaign-2 Theme-3 Wave-1 — risk detail destination (RSK-NN chips deep-link here).
const InterlockRiskDetail = lazy(() => import('./pages/interlock/RiskDetailPage'));
// Campaign-2 Theme-3 Wave-2 (E3) — risk register index (browse-into destination
// for InterlockRiskDetail; previously only reachable via a chip elsewhere).
const InterlockRiskRegister = lazy(() => import('./pages/interlock/RiskRegisterPage'));
// UX Wave C2 — NEW first-class FMEA page (Excel-familiar unified table of
// every FAILURE_MODE, distinct from the legacy AgGrid grid and the workshop
// worksheet — both of those stay put; this is a new browsable index).
const InterlockFmeaPage = lazy(() => import('./pages/interlock/FmeaPage'));
// Your-court FMEA review — read-only landing a "waiting on me" fmea-risk row
// deep-links to (HomePage.tsx's waitingItemHref), one door short of the
// Root Cause Workshop below. See FmeaReviewPage.tsx header for the why.
const InterlockFmeaReviewPage = lazy(() => import('./pages/interlock/FmeaReviewPage'));
// Campaign-2 Theme-2 Wave-5 (audit finding #5) — project-wide escalations register.
const InterlockEscalationsPage = lazy(() => import('./pages/interlock/EscalationsPage'));
// UX Wave A1 — admin settings (user roster / role management), linked from the
// rail profile popover for an ADMIN actor.
const InterlockAdminPage = lazy(() => import('./pages/interlock/AdminPage'));
// UX Wave 1 — settings (profile + notification prefs), linked from the rail
// profile popover for every actor.
const InterlockSettingsPage = lazy(() => import('./pages/interlock/SettingsPage'));
// B2 (nav pipeline) — pipeline stage 2: the SRS requirements manager/drill-down
// adopted into the Interlock shell (Option M). Legacy /requirements redirects here.
const InterlockRequirementsPage = lazy(() => import('./pages/interlock/RequirementsPage'));
// Capability-gating sweep — catch-all 404 for any unmatched /interlock/* path
// (see the trailing wildcard route below, and RedirectToRequirementFocus above).
const InterlockNotFoundPage = lazy(() => import('./pages/interlock/InterlockNotFoundPage'));
// D5 (doc round-trip P5) — FAT/SAT test plan. Door-reached (no rail entry;
// rail placement is an open owner question).
const InterlockTestsPage = lazy(() => import('./pages/interlock/TestsPage'));
// The FAT/SAT "Run" tester surface (run-list home + single-test capture screen).
// W5/W6 promoted the run list to BE /interlock/acceptance — it was reachable
// only as a tablet bookmark, which is why the owner could not find where FAT
// results get entered. /interlock/run still resolves; it is the same page.
const InterlockRunListPage = lazy(() => import('./pages/interlock/RunListPage'));
const InterlockRunCapturePage = lazy(() => import('./pages/interlock/RunCapturePage'));
const InterlockEndorsementPage = lazy(() => import('./pages/interlock/EndorsementPage'));
// Phase-2 Slice-4 — the PM COMMAND DECK. A management surface, and since W5/W6
// it sits BEHIND a door at /interlock/acceptance/deck rather than being the
// acceptance page itself. Not /interlock/campaign because /interlock/campaigns
// is the unrelated SCORING page, and one letter apart would have read as the
// same place. /interlock/campaign redirects to the acceptance page regardless.
const InterlockCampaignDeckPage = lazy(() => import('./pages/interlock/CampaignDeckPage'));
// Build-phase execution surfaces (2026-07-28). Separate routes, not tabs: the
// responsibility owner changes between them at the handover gate.
const InterlockBuildPage = lazy(() => import('./pages/interlock/BuildPage'));
// One subsystem's ITP, executable. Its own route (not a modal on the landing)
// so a bench tablet can bookmark the plan it is working today.
const InterlockBuildSubsystemPage = lazy(() => import('./pages/interlock/BuildSubsystemPage'));
const InterlockCommissioningPage = lazy(() => import('./pages/interlock/CommissioningPage'));
// Search gap fix — full, shareable/bookmarkable results page for the nav-rail
// combobox (GlobalSearch.tsx), reached via its new "See all results" footer
// link / Enter-with-nothing-highlighted, or a direct `/search?q=` bookmark
// (redirected in below, see LegacyTopLevelSearchRedirect).
const InterlockSearchResultsPage = lazy(() => import('./pages/interlock/SearchResultsPage'));
// FEA-060: Project Mission Page
const ProjectMissionPage = lazy(() => import('./pages/ProjectMissionPage'));

// Main FMEA workflow pages
const CauseEffectPage = lazy(() =>
  import('./pages/CauseEffectPage').then((m) => ({ default: m.CauseEffectPage }))
);
// F5: single canonical Risk Review page that consolidates the six overlapping risk views
const RiskReviewPage = lazy(() =>
  import('./pages/RiskReviewPage').then((m) => ({ default: m.RiskReviewPage }))
);
const VisualizationsPage = lazy(() =>
  import('./pages/VisualizationsPage').then((m) => ({ default: m.VisualizationsPage }))
);

// Dashboard and analytics pages

// Data management pages
const ImportWizard = lazy(() => import('./pages/ImportWizard'));
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })));
const AuditTrail = lazy(() =>
  import('./pages/AuditTrail').then((m) => ({ default: m.AuditTrail }))
);
const TemplateManager = lazy(() =>
  import('./pages/TemplateManager').then((m) => ({ default: m.TemplateManager }))
);
const TemplateLibraryPage = lazy(() => import('./pages/TemplateLibraryPage'));

// Requirements Traceability pages (Phase 14 - Gap 5)
// B2 (nav pipeline) — RequirementsPage moved INTO the Interlock shell
// (pages/interlock/RequirementsPage.tsx, lazy-imported above); the legacy
// /requirements route now redirects (see LegacyRequirementsRedirect).
const TestPlansPage = lazy(() => import('./pages/TestPlansPage'));
const CoverageMatrixPage = lazy(() => import('./pages/CoverageMatrixPage'));
const TraceabilityReportPage = lazy(() => import('./pages/TraceabilityReportPage'));

// Entity management pages (Phase 15)
const MitigationsPage = lazy(() =>
  import('./pages/MitigationsPage').then((m) => ({ default: m.MitigationsPage }))
);
// Legacy /interfaces route now redirects (see LegacyInterfacesRedirect) — the
// editable ComponentConnection-backed page it used to render was a duplicate
// of the canonical Interlock interface register that disagreed with it on
// classification (derived internal/external vs Interface.register) and had no
// cross-link. Removed; see dev/active/legacy-interfaces-removal-plan.md.
const SystemStatesPage = lazy(() =>
  import('./pages/SystemStatesPage').then((m) => ({ default: m.SystemStatesPage }))
);

// Settings and configuration pages
const FailureModeTemplatesPage = lazy(() => import('./pages/FailureModeTemplatesPage'));

// Reports pages (FEA-005, FEA-006, FEA-007, FEA-008)
const ReportsPage = lazy(() =>
  import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage }))
);
const NewReportWizard = lazy(() =>
  import('./components/reports/NewReportWizard').then((m) => ({ default: m.NewReportWizard }))
);
const ReportJobDetails = lazy(() =>
  import('./components/reports/ReportJobDetails').then((m) => ({ default: m.ReportJobDetails }))
);
const ReportHistory = lazy(() =>
  import('./components/reports/ReportHistory').then((m) => ({ default: m.ReportHistory }))
);

// Stress Library page (FEA-055)
const StressLibraryPage = lazy(() =>
  import('./components/stress/StressLibraryManager').then((m) => ({
    default: m.StressLibraryManager,
  }))
);

// Risk Overview page (FEA-056)

// Query-string-preserving redirect (2026-07-29 sweep). Several routes still did
// a bare `<Navigate to="/some/path" replace />`, which throws the whole query
// string away — including `?actAs=`, the dev impersonation switch. The failure
// is silent and actively misleading: the impersonated identity is lost, the
// backend's `resolveActor` falls back to `local-admin`/`req.user`, and the
// resulting capability mismatch reads as a real authorisation bug rather than
// as the lost query param it actually is (see packages/frontend/src/CLAUDE.md,
// "Redirects/`<Navigate>` must PRESERVE the query string"). Use this instead of
// a bare string target for every STATIC redirect; redirects that need to SET a
// param build a URLSearchParams from `location.search` first
// (LegacyWorkshopRedirect and friends below) so every incoming param — `actAs`
// above all — rides along.
function PreserveQueryRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

// FMEA F4 retirement (owner decision, 2026-07-14): the legacy shell is NOT in
// active use, so the four standalone workshop routes (/function-workshop,
// /fm-workshop, /causes-workshop, /mitigations-workshop — plus the older
// /rc-workshop alias) redirect UNCONDITIONALLY into the /interlock/fmea tab
// shell. The page components themselves are NOT dead: the FMEA tabs embed them
// (FmeaFunctionsTab → FunctionWorkshopPage etc.) — only their standalone
// top-level routes are retired. Any query string on the legacy URL is carried
// across (the standalone pages read no params themselves — scope travels via
// HierarchyNavigationContext sessionStorage — but a hypothetical ?scope=
// bookmark lands scoped in the shell).
function LegacyWorkshopRedirect({ tab }: { tab: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', tab);
  return <Navigate to={`/interlock/fmea?${params.toString()}`} replace />;
}

// Nav B3 (2026-07-15) promoted the FUNCTIONS stage out of the FMEA tab shell
// to its own /interlock/functions page. Owner call on 2026-07-21 reversed
// that (Stage 1 of the FMEA workflow IS function definition — see
// FmeaPage.tsx's header), so the page and its rail entry are gone. This
// keeps the URL alive as a redirect (old bookmarks/links must not 404),
// carrying every query param (`?scope=` above all) across to the FMEA
// shell's FUNCTIONS tab.
function RedirectFunctionsToFmea() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'functions');
  return <Navigate to={`/interlock/fmea?${params.toString()}`} replace />;
}

// B2 (nav pipeline) — legacy /requirements retired into the Interlock shell
// (same F4 redirect precedent as the workshop routes above). The query string
// MUST ride along: `?focus=<id>` is the Z9 deep-link contract every bound
// chip and old bookmark relies on.
function LegacyRequirementsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/interlock/requirements', search: location.search }} replace />;
}

// Legacy duplicate interface register removal (owner decision) — the old
// top-level `/interfaces` page rendered a second, editable ICD register
// directly on top of `ComponentConnection` (the block-diagram edge table),
// with its own derived internal/external split. That disagreed with the
// canonical Interlock interface register (the `Interface` model, its own
// register/status/owner fields) and had no cross-link to it — a
// data-integrity hazard, not a second valid source. `ComponentConnection`
// itself stays (block diagram still owns it); only the duplicate editable UI
// is gone. Same carry-the-querystring idiom as LegacyRequirementsRedirect
// above; lands on `/interlock/interfaces`, which itself redirects on to
// `/interlock/matrix?view=list`.
function LegacyInterfacesRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/interlock/interfaces', search: location.search }} replace />;
}

// Parcel P7 (nav promotions) — `/audit` and `/stress-library` were the last
// two legacy (non-Interlock) shell pages left rendering the old Breadcrumb
// chrome instead of NavRail. Same carry-the-querystring redirect idiom as
// LegacyRequirementsRedirect/LegacyInterfacesRedirect above — the components
// themselves (AuditTrail, StressLibraryManager) are unchanged/reused, just
// remounted under /interlock/* wrapped in InterlockScaffold below.
function LegacyAuditRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/interlock/audit', search: location.search }} replace />;
}

function LegacyStressLibraryRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/interlock/stress', search: location.search }} replace />;
}

// Dogfood bug #5 (MINOR) — `/interlock/risk/:ref` (singular) never matched a
// route, so it fell through to the `/interlock/*` catch-all and silently
// bounced to the Interlock home page. Redirect the plausible-but-wrong
// singular form to the real plural route instead.
function RedirectToPluralRisk() {
  const { ref } = useParams<{ ref: string }>();
  const location = useLocation();
  return (
    <Navigate to={{ pathname: `/interlock/risks/${ref ?? ''}`, search: location.search }} replace />
  );
}

// Capability-gating sweep / 404 fix — `/interlock/requirements/:id` had no
// route at all (RequirementsManager's own detail view is a `?focus=<id>`
// drill-down on the LIST page, not a separate URL), so any deep link/bookmark
// to it fell through to the old unconditional `/interlock/*` catch-all and
// silently bounced to the dashboard. RequirementsPage already supports
// `?focus=<id>` (see its Z9 deep-link effect) — this makes the requirement
// detail URL actually resolve (survives reload, is bookmarkable) instead of
// needing a bigger drill-down-as-its-own-route refactor.
function RedirectToRequirementFocus() {
  const { id } = useParams<{ id: string }>();
  // Build on top of the INCOMING query string rather than replacing it: a bare
  // `?focus=` template dropped every other param, `?actAs=` included.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('focus', id ?? '');
  return <Navigate to={`/interlock/requirements?${params.toString()}`} replace />;
}

// Search gap fix — the nav-rail search box's natural full-results URL is
// top-level `/search?q=`, but every Interlock screen renders inside the
// `/interlock/*` branch (its own NavRail/chrome, no legacy shell — see the
// block comment on `location.pathname.startsWith('/interlock')` below). A bare
// `/search` used to 404 outright (fell through the legacy catch-all). Same
// carry-the-querystring redirect idiom as `/components` and
// LegacyRequirementsRedirect above.
function LegacyTopLevelSearchRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/interlock/search', search: location.search }} replace />;
}

// Risk-cluster query-string drop fix (HIGH) — the six legacy risk routes below
// used to `<Navigate to="hardcoded-target">` with no search string at all,
// silently dropping any query param on the URL — including `?actAs=`, the dev
// impersonation switch. A bookmarked/shared legacy risk link with `?actAs=`
// would land the impersonating user back on their OWN session, which then
// looks like a stale-id capability 403 instead of what it actually is (a lost
// query string). Same carry-the-querystring idiom as LegacyRequirementsRedirect
// above: merge the incoming query string with the route's own `tab=` param
// (existing `tab` from the URL, if any, is overridden by the route's target
// tab; every other param — including `actAs` — rides along unchanged).
function LegacyRiskRedirect({ tab }: { tab?: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (tab) {
    params.set('tab', tab);
  }
  const qs = params.toString();
  return <Navigate to={`/risk-review${qs ? `?${qs}` : ''}`} replace />;
}

// Workshop Summary Report page (FEA-121)
const WorkshopSummaryReportPage = lazy(() =>
  import('./pages/WorkshopSummaryReportPage').then((m) => ({
    default: m.WorkshopSummaryReportPage,
  }))
);

// Software Logic Workshop page (FEA-134)
const SoftwareLogicWorkshopPage = lazy(() =>
  import('./pages/SoftwareLogicWorkshopPage').then((m) => ({
    default: m.SoftwareLogicWorkshopPage,
  }))
);

// Sub-System Risk Report page (FEA-144)

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="page-loading-fallback">
    <div className="loading-spinner" />
    <p>Loading...</p>
  </div>
);

/**
 * Extract user-friendly error message from API errors
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Check for common API error patterns
    const message = error.message;

    // Network errors
    if (message.includes('fetch') || message.includes('network')) {
      return 'Unable to connect to server. Please check your connection.';
    }

    // Timeout errors
    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }

    // Return the actual error message if it's reasonably short
    if (message.length < 100) {
      return message;
    }

    return 'An unexpected error occurred. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only show error toast if query has already been retried
      // This prevents showing errors for transient issues
      if (query.state.dataUpdateCount > 0) {
        toast.error(getErrorMessage(error), {
          id: `query-error-${query.queryHash}`,
          duration: 5000,
        });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Opt-out: mutations that surface their own error toast (e.g. a dialog's
      // catch block calling showMutationErrorToast) set
      // `meta: { skipGlobalErrorToast: true }` so the user gets ONE toast, not
      // two. Without this the global handler double-fires alongside the
      // component's own toast (duplicate-error-toast bug).
      if (mutation.meta?.skipGlobalErrorToast) return;
      toast.error(getErrorMessage(error), {
        duration: 5000,
      });
    },
  }),
  defaultOptions: {
    queries: {
      // FEA-129: Aggressive refetching for multi-user support
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch after network recovery
      retry: 1,
      staleTime: 10000, // 10 seconds default (was 30s)
    },
  },
});

function AppContent() {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
  const [_isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Get current location for route-based keying (forces remount on route change)
  const location = useLocation();

  // Get AI chat permission from auth context
  const { canAccessAIChat } = useAuth();

  // Load and apply user settings
  const { settings } = useSettings();

  // Show wizard on first visit
  useFirstVisit();

  // Register global keyboard shortcuts (memoize options to prevent re-render loops)
  const shortcutOptions = useMemo(
    () => ({
      onOpenCommandPalette: () => setIsCommandPaletteOpen(true),
      onOpenShortcutsHelp: () => setIsShortcutsHelpOpen(true),
      onToggleSidebar: () => setIsSidebarCollapsed((prev: boolean) => !prev),
    }),
    []
  );
  const { shortcuts } = useKeyboardShortcuts(shortcutOptions);

  // Apply appearance settings globally
  useEffect(() => {
    const root = document.documentElement;

    // Apply theme
    if (settings.appearance.theme === 'dark') {
      document.body.classList.add('dark-mode');
    } else if (settings.appearance.theme === 'light') {
      document.body.classList.remove('dark-mode');
    } else {
      // Auto mode - follow system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.body.classList.toggle('dark-mode', prefersDark);
    }

    // Apply font size
    root.style.fontSize = `${settings.appearance.fontSize}px`;

    // Apply primary color
    root.style.setProperty('--primary-color', settings.appearance.primaryColor);

    // Apply compact mode
    document.body.classList.toggle('compact-mode', settings.appearance.compactMode);

    // Apply sidebar position
    document.body.classList.toggle(
      'sidebar-right',
      settings.appearance.sidebarPosition === 'right'
    );

    // Apply grid lines setting to tables
    if (settings.appearance.showGridLines) {
      document.body.classList.add('show-grid-lines');
    } else {
      document.body.classList.remove('show-grid-lines');
    }
  }, [
    settings.appearance.theme,
    settings.appearance.fontSize,
    settings.appearance.primaryColor,
    settings.appearance.compactMode,
    settings.appearance.sidebarPosition,
    settings.appearance.showGridLines,
  ]);

  // Interlock screens (design 13a) carry their own nav rail/shell — render them
  // without the legacy FMEA Studio chrome. See dev/active/interface-pivot/.
  if (location.pathname.startsWith('/interlock')) {
    return (
      // RS1 (round 10 resilience) — the interlock shell used to return BEFORE the
      // PageErrorBoundary that wraps every legacy route below, so a render throw on
      // any /interlock/* screen white-screened the whole app instead of showing a
      // recoverable "something broke" state. Wrap it the same way the legacy routes
      // already are.
      <PageErrorBoundary key={location.pathname}>
        {/* UX Wave C2 fix-round 2 (persona-audit MAJOR 1) — react-hot-toast's
            <Toaster> was only mounted in the legacy-shell return below, so every
            showToast.* call from an /interlock page (inline risk edits, intake
            modal saves, FMEA severity edits) fired into a void: the mutation
            hooks' onSuccess/onError toasts silently never rendered. Mount the
            same accessible container (identical props to the legacy one) inside
            the interlock branch. */}
        <Toaster
          position="top-right"
          containerStyle={{ zIndex: 99999 }}
          toastOptions={{
            ariaProps: { role: 'status', 'aria-live': 'polite' },
            duration: 5000,
          }}
        />
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route path="/interlock" element={<InterlockHomePage />} />
            <Route path="/interlock/interfaces" element={<InterlockRegisterPage />} />
            <Route path="/interlock/interfaces/:refId" element={<InterlockInterfaceDetail />} />
            {/* Ontology O4 — component page (INSIDE / PROMISED / DECIDED). */}
            <Route path="/interlock/components/:id" element={<InterlockComponentPage />} />
            {/* Component Spec-Sheet v1 — Components nav list + per-component spec sheet. */}
            <Route path="/interlock/components-list" element={<InterlockComponentsListPage />} />
            <Route path="/interlock/components/:id/spec" element={<InterlockComponentSpecPage />} />
            <Route path="/interlock/risks" element={<InterlockRiskRegister />} />
            <Route path="/interlock/risks/:ref" element={<InterlockRiskDetail />} />
            {/* Dogfood bug #5 (MINOR) — the singular `/interlock/risk/:ref` (no "s")
                matched nothing above and fell through to the `/interlock/*` catch-all
                below, silently bouncing to the Interlock home page with no
                indication anything was wrong. Same DL-1 (R18 Parcel B) precedent as
                the trades splat above: redirect the plausible-but-wrong singular form
                to the real route instead of a silent bounce. */}
            <Route path="/interlock/risk/:ref" element={<RedirectToPluralRisk />} />
            {/* Persona-audit Loop 1 (MINOR) — the bare, ref-less `/interlock/risk`
                also matched nothing (":ref" above requires a segment), so it fell
                through to the `/interlock/*` catch-all and silently bounced home
                too. Same fix as the singular-with-ref case just above. */}
            <Route
              path="/interlock/risk"
              element={<PreserveQueryRedirect to="/interlock/risks" />}
            />
            {/* UX Wave C2 — NEW top-level FMEA page. */}
            <Route path="/interlock/fmea" element={<InterlockFmeaPage />} />
            {/* Your-court FMEA review — read-only door in front of the Root
                Cause Workshop; see FmeaReviewPage.tsx. */}
            <Route path="/interlock/fmea/review/:fmId" element={<InterlockFmeaReviewPage />} />
            <Route path="/interlock/matrix" element={<InterlockMatrixPage />} />
            <Route path="/interlock/system" element={<InterlockSystemViewPage />} />
            {/* Mission Map retired (owner decision, punch-list batch) — redundant
                now the proper FMEA workshop is back. Redirect (F4 precedent, not
                a 404) so bookmarks/deep-links land somewhere sane. The page
                (MissionMapPage.tsx) is DELETED; git history keeps it. The backend
                mission-map endpoint stays live — TradeDetailPage still joins
                pull provenance via fetchMissionMap(). */}
            <Route
              path="/interlock/mission"
              element={<PreserveQueryRedirect to="/interlock/fmea" />}
            />
            {/* B2 (nav pipeline) — pipeline stage 2. The real page (B1 shipped
                a thin redirect placeholder here). */}
            <Route path="/interlock/requirements" element={<InterlockRequirementsPage />} />
            {/* Capability-gating sweep / 404 fix — see RedirectToRequirementFocus's
                doc comment above: makes the requirement-detail URL resolve
                (reload-safe, bookmarkable) instead of silently bouncing home. */}
            <Route path="/interlock/requirements/:id" element={<RedirectToRequirementFocus />} />
            {/* D5 — FAT/SAT plan page; reached via the requirements-page door
                and requirement-chip backlinks (no rail entry by design). */}
            <Route path="/interlock/tests" element={<InterlockTestsPage />} />
            {/* Phase-2 Slice-2 — FAT/SAT run-sheet (tablet) + single-test capture. */}
            <Route path="/interlock/run" element={<InterlockRunListPage />} />
            {/* Static segment declared before the dynamic one — React Router
                ranks static higher, but keeping them adjacent makes the intent
                obvious: /run/endorsements is a page, not a testPlanId. */}
            <Route path="/interlock/run/endorsements" element={<InterlockEndorsementPage />} />
            <Route path="/interlock/run/:testPlanId" element={<InterlockRunCapturePage />} />
            {/* Phase-2 Slice-4 — the PM command deck. The tab and the phase ride
                query params (?tab=rvtm&phase=SAT) so any view is bookmarkable and
                reload-safe, same idiom as the matrix page's ?view=. */}
            <Route path="/interlock/build" element={<InterlockBuildPage />} />
            <Route path="/interlock/build/:componentId" element={<InterlockBuildSubsystemPage />} />
            <Route path="/interlock/commissioning" element={<InterlockCommissioningPage />} />
            {/* W5/W6 — the acceptance page is INVERTED. It used to open on the
                eleven-section PM deck, which is a management dashboard wearing
                the name of an execution phase: the owner could not find where
                results get entered. The run list is now the page, and the deck
                is a door on it at /interlock/acceptance/deck. Nothing was
                deleted (ui-plan.md, "Acceptance — rework"). */}
            <Route path="/interlock/acceptance" element={<InterlockRunListPage />} />
            <Route path="/interlock/acceptance/deck" element={<InterlockCampaignDeckPage />} />
            {/* The name the deck is described by in the SOW. Kept as a redirect
                so that link resolves, and so nobody lands on the SCORING page at
                /interlock/campaigns by typing the singular. Points at the DECK,
                which is what the SOW meant by "campaign", not at the run list
                that now holds /interlock/acceptance. */}
            <Route
              path="/interlock/campaign"
              element={<PreserveQueryRedirect to="/interlock/acceptance/deck" />}
            />
            {/* Retired 2026-07-21 (see RedirectFunctionsToFmea's header): Functions
                is Stage 1 of the FMEA workflow, not its own page — redirect old
                bookmarks/links into the FMEA shell's FUNCTIONS tab, scope kept. */}
            <Route path="/interlock/functions" element={<RedirectFunctionsToFmea />} />
            <Route path="/interlock/gates" element={<InterlockGatePage />} />
            {/* Parcel P2 — project-status landing page. */}
            <Route path="/interlock/status" element={<InterlockStatusPage />} />
            {/* W5 — per-person workload view (management-gated server-side). */}
            <Route path="/interlock/workload" element={<InterlockWorkloadPage />} />
            {/* Parcel P7 — Audit + Stress library promoted into the Interlock
                shell. AuditTrail/StressLibraryManager render their own inner
                content only now (outer chrome/Breadcrumb dropped); the
                NavRail/topbar comes from InterlockScaffold here, same as
                every other /interlock/* page. */}
            <Route
              path="/interlock/audit"
              element={
                <InterlockScaffold
                  nav="audit"
                  title="Audit Trail"
                  subtitle="Complete audit log of all system changes"
                >
                  <AuditTrail />
                </InterlockScaffold>
              }
            />
            <Route
              path="/interlock/stress"
              element={
                <InterlockScaffold nav="stress" title="Stress cases">
                  <StressLibraryPage />
                </InterlockScaffold>
              }
            />
            <Route path="/interlock/campaigns" element={<InterlockCampaignsPage />} />
            <Route path="/interlock/campaigns/:id" element={<InterlockCampaignDetailPage />} />
            {/* FMEA F4 retirement (owner decision) — /interlock/workshop
                redirects to the FMEA tab shell. NOTE what this retires: the
                route hosted the session-based sweep workshop (START → SWEEP →
                countersign REVIEW). Punch-list batch 2 finished the prune:
                pages/interlock/WorkshopPage.tsx + the fmea-session API fns are
                DELETED (git history keeps them) and the backend session-create
                route returns 410 Gone. Its two in-product consumers were
                re-homed earlier: FmeaPage's "Open workshop →" action (removed)
                and interlockApi's deepLinkFor('review') (now lands here and
                follows the redirect). If session countersign review is ever
                needed again it must be re-surfaced INSIDE the FMEA shell. */}
            <Route
              path="/interlock/workshop"
              element={<PreserveQueryRedirect to="/interlock/fmea" />}
            />
            {/* Search gap fix — full-results page for the nav-rail GlobalSearch
                combobox; no rail entry (reached via the search box itself, its
                "See all results" footer link, Enter with nothing highlighted,
                or a direct `?q=` bookmark). */}
            <Route path="/interlock/search" element={<InterlockSearchResultsPage />} />
            <Route path="/interlock/escalations" element={<InterlockEscalationsPage />} />
            <Route path="/interlock/admin" element={<InterlockAdminPage />} />
            <Route path="/interlock/settings" element={<InterlockSettingsPage />} />
            {/* DL-1 (R18 Parcel B) — splat, not `:refId`: a `:refId` segment can
                never match a refId containing a literal `/` (e.g. REC/INT-10-T1),
                so a raw pasted/bookmarked link silently fell through to the
                `/interlock/*` catch-all below and bounced to home with no
                "not found". TradeDetailPage reads the full remainder via
                `useParams()['*']` and decodeURIComponent()s it. */}
            <Route path="/interlock/trades/*" element={<InterlockTradeDetailPage />} />
            {/* Capability-gating sweep / MAJOR fix — this used to be
                `<Navigate to="/interlock" replace />`, so ANY unmatched path
                under /interlock (garbage URLs, `/interlock/gates/:id`,
                `/interlock/fmea/1`, typos, stale bookmarks) silently bounced
                to the dashboard with zero indication anything was wrong.
                Render the in-app not-found instead — same idiom several
                detail pages (InterfaceDetailPage, RiskDetailPage,
                TradeDetailPage, ComponentPage) already use for an unresolved
                ENTITY, now applied at the ROUTER level for an unresolved
                PATH. */}
            <Route path="/interlock/*" element={<InterlockNotFoundPage />} />
          </Routes>
        </Suspense>
      </PageErrorBoundary>
    );
  }

  return (
    <>
      <div className="App">
        <Navigation />

        {/* G2: Persistent product header — page title + active project name.
            UF-3: also hosts the always-visible Cmd+K search affordance, wired
            to the same open handler as the keyboard shortcut. */}
        <AppHeader onOpenCommandPalette={() => setIsCommandPaletteOpen(true)} />

        <div className="app-layout">
          {/* QuickNavSidebar disabled - conflicts with left side navigation bar which is better.
              The new icon-based left sidebar provides cleaner navigation without the need
              for an additional quick-nav panel. Can be re-enabled later if needed. */}
          {/* <QuickNavSidebar /> */}

          <main id="main-content" className="App-main" role="main" aria-label="Main content">
            {/* LOW-1: Page-level error boundary catches React errors in page components */}
            <PageErrorBoundary>
              {/* Suspense boundary for lazy-loaded routes - shows loading state while chunks load */}
              <Suspense fallback={<PageLoadingFallback />}>
                {/* Key on location.pathname forces remount on route change - fixes ReactFlow navigation issues */}
                <Routes key={location.pathname}>
                  {/* sysview-wave2 — landing + legacy Block Diagram both redirect
                      into the Interlock shell now that editing lives there too.
                      Route kept (not deleted) so bookmarks/deep-links to
                      `/components` still resolve, per the legacy-redirect
                      precedent below (`/risk-assessment` etc). The search string
                      is forwarded (Opus review, HIGH): live callers deep-link with
                      `?selected=` (CauseEffect pages), `?missing=` (Completeness
                      drill-downs), `?component=&edit=true`/`?create=true`
                      (TierEditPanel) — SystemViewPage consumes them on arrival. */}
                  <Route
                    path="/"
                    element={
                      <Navigate to={{ pathname: '/interlock', search: location.search }} replace />
                    }
                  />
                  <Route
                    path="/components"
                    element={
                      <Navigate
                        to={{ pathname: '/interlock/system', search: location.search }}
                        replace
                      />
                    }
                  />
                  {/* Search gap fix — the real page lives at `/interlock/search`
                      (Interlock's own chrome); this keeps the plain, more
                      guessable `/search?q=` URL resolving instead of 404ing. */}
                  <Route path="/search" element={<LegacyTopLevelSearchRedirect />} />
                  <Route path="/cause-effect" element={<CauseEffectPage />} />
                  {/* F5: canonical consolidated risk entry point */}
                  <Route path="/risk-review" element={<RiskReviewPage />} />
                  {/* Legacy risk routes redirect into the consolidated /risk-review tabs
                      (deep links / Cmd+K still resolve). Query string (incl. dev
                      `?actAs=` impersonation) is preserved via LegacyRiskRedirect. */}
                  <Route path="/risk-assessment" element={<LegacyRiskRedirect tab="matrix" />} />
                  <Route path="/risk-dashboard" element={<LegacyRiskRedirect />} />
                  <Route
                    path="/executive-dashboard"
                    element={<LegacyRiskRedirect tab="executive" />}
                  />
                  <Route path="/visualizations" element={<VisualizationsPage />} />
                  <Route path="/import" element={<ImportWizard />} />
                  <Route path="/completeness" element={<LegacyRiskRedirect tab="completeness" />} />
                  {/* Parcel P7 — promoted into the Interlock shell; see
                      LegacyAuditRedirect above. */}
                  <Route path="/audit" element={<LegacyAuditRedirect />} />
                  {/* R22 (Returning-user) — the legacy NotificationsPage calls the
                      stale /api/notifications with a hardcoded userId='you', so it
                      renders "All caught up" for EVERY real user regardless of their
                      actual unread/pending state — a routed, bookmarkable trap that
                      lies about whether anything needs you. The live, identity-aware
                      notification surface is the Interlock bell; redirect there until
                      a real page is (re)built rather than leave a page that misleads. */}
                  <Route
                    path="/notifications"
                    element={<PreserveQueryRedirect to="/interlock" />}
                  />
                  <Route path="/templates" element={<TemplateManager />} />
                  <Route path="/template-library" element={<TemplateLibraryPage />} />
                  {/* Phase 14 - Gap 5: Requirements Traceability. B2 (nav
                      pipeline) — the page moved into the Interlock shell;
                      redirect preserves ?focus= (Z9 deep-link contract). */}
                  <Route path="/requirements" element={<LegacyRequirementsRedirect />} />
                  <Route path="/test-plans" element={<TestPlansPage />} />
                  <Route path="/coverage-matrix" element={<CoverageMatrixPage />} />
                  <Route path="/traceability-report" element={<TraceabilityReportPage />} />
                  {/* Phase 15: Cross-Tab Navigation - Missing Entity Pages */}
                  <Route path="/mitigations" element={<MitigationsPage />} />
                  {/* Wave 14.1: Error Paths Page (SoW-224) - kept adjacent domain */}
                  {/* Legacy duplicate interface register — redirects to the canonical
                      Interlock register; see LegacyInterfacesRedirect above. */}
                  <Route path="/interfaces" element={<LegacyInterfacesRedirect />} />
                  <Route path="/system-states" element={<SystemStatesPage />} />
                  {/* Wave 15.5: Failure Mode Templates (SoW-271d) */}
                  <Route path="/failure-mode-templates" element={<FailureModeTemplatesPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/history" element={<ReportHistory />} />
                  <Route path="/reports/new" element={<NewReportWizard />} />
                  <Route path="/reports/:jobId" element={<ReportJobDetails />} />
                  {/* FEA-134: Software Logic Workshop */}
                  <Route path="/software-logic-workshop" element={<SoftwareLogicWorkshopPage />} />
                  {/* FEA-055: Stress Library */}
                  {/* Parcel P7 — promoted into the Interlock shell; see
                      LegacyStressLibraryRedirect above. */}
                  <Route path="/stress-library" element={<LegacyStressLibraryRedirect />} />
                  {/* FEA-056: Risk Overview — folded into /risk-review */}
                  <Route path="/risk-overview" element={<LegacyRiskRedirect tab="overview" />} />
                  {/* FMEA F4 retirement (owner decision) — the standalone
                      workshop routes redirect UNCONDITIONALLY into the
                      /interlock/fmea tab shell (legacy shell is not in active
                      use). The workshop PAGES live on, embedded in the FMEA
                      tabs. Legacy-shell links that still point here
                      (navigationConfig, entitySearchCommands, dashboard CTAs,
                      SoftwareLogicWorkshopPage, CauseEffectSidePanel) now land
                      in the shell too. See LegacyWorkshopRedirect above;
                      proof: testing/scripts/proof-fmea-f4-close.mjs. */}
                  <Route
                    path="/causes-workshop"
                    element={<LegacyWorkshopRedirect tab="causes" />}
                  />
                  <Route
                    path="/function-workshop"
                    element={<LegacyWorkshopRedirect tab="functions" />}
                  />
                  <Route
                    path="/fm-workshop"
                    element={<LegacyWorkshopRedirect tab="failure-modes" />}
                  />
                  {/* FEA-065's old /rc-workshop alias chains to the same place. */}
                  <Route path="/rc-workshop" element={<LegacyWorkshopRedirect tab="causes" />} />
                  <Route
                    path="/mitigations-workshop"
                    element={<LegacyWorkshopRedirect tab="mitigations" />}
                  />
                  {/* FEA-060: Project Mission Page */}
                  <Route path="/mission" element={<ProjectMissionPage />} />
                  {/* FEA-121: Workshop Summary Report */}
                  <Route path="/workshop-report" element={<WorkshopSummaryReportPage />} />
                  {/* FEA-144: Sub-System Risk Report — folded into /risk-review */}
                  <Route
                    path="/subsystem-risk-report"
                    element={<LegacyRiskRedirect tab="subsystem" />}
                  />
                  {/* FEA-067: Old routes - redirect for backwards compatibility */}
                  <Route
                    path="/traceability"
                    element={<PreserveQueryRedirect to="/traceability-report" />}
                  />
                  {/* Catch-all: unknown routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </PageErrorBoundary>
          </main>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onOpenShortcutsHelp={() => {
          setIsCommandPaletteOpen(false);
          setIsShortcutsHelpOpen(true);
        }}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp
        isOpen={isShortcutsHelpOpen}
        onClose={() => setIsShortcutsHelpOpen(false)}
        shortcuts={shortcuts}
      />

      {/* Accessible toast notifications with aria-live */}
      <Toaster
        position="top-right"
        containerStyle={{
          zIndex: 99999, // Ensure toasts appear above modals and side panels
        }}
        toastOptions={{
          // Ensure toasts are announced to screen readers
          ariaProps: {
            role: 'status',
            'aria-live': 'polite',
          },
          duration: 5000,
        }}
      />
      {/* React Query DevTools - disabled to clear bottom-right for AI chat FAB
          Can be re-enabled via Settings → Developer Mode if needed */}
      {/* {(process.env.NODE_ENV === 'development' || settings.advanced.developerMode) &&
        !navigator.webdriver && <ReactQueryDevtools initialIsOpen={false} />} */}

      {/* FMEA Creation Wizard */}
      <WizardShell />

      {/* Migration Welcome Modal - Shows once for existing users */}
      <MigrationWelcomeModal />

      {/* FEA-024: Terminal Chat Panel (Claude Code embedded)
          Only shown if user has AI chat permission (ADMIN or local admin) */}
      {canAccessAIChat && <TerminalChatPanel />}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      {/* FEA-143: TerminologyProvider - FMEA/HAZID terminology toggle */}
      <TerminologyProvider>
        <QueryClientProvider client={queryClient}>
          {/* FEA-125: AuthProvider provides user context and permissions
              Must be inside QueryClientProvider (uses useQuery) */}
          <AuthProvider>
            <WizardProvider>
              <ProjectProvider>
                <QuickNavProvider>
                  <BrowserRouter>
                    {/* HierarchyNavigationProvider persists block diagram drill-down state
                      across workshop pages (Function, FM, Causes, Mitigations) */}
                    <HierarchyNavigationProvider>
                      {/* AIContextProvider must be inside BrowserRouter (uses useLocation)
                        and inside ProjectProvider (uses useProject) */}
                      <AIContextProvider>
                        <AppContent />
                      </AIContextProvider>
                    </HierarchyNavigationProvider>
                  </BrowserRouter>
                </QuickNavProvider>
              </ProjectProvider>
            </WizardProvider>
          </AuthProvider>
        </QueryClientProvider>
      </TerminologyProvider>
      {/* Mounted at the app root (outside all providers) so it survives every
          route and provider re-render. Shows the git SHA + build age of the
          currently-loaded bundle — pattern + methodology mirrored from
          Taskalotl's docs/build-version-badge.md. Pinned bottom-right here
          (not bottom-left like Taskalotl) because Interlock's nav rail
          already docks a user/profile chip in that corner — see
          BuildBadge.css for the placement rationale. */}
      <BuildBadge />
    </ThemeProvider>
  );
}

export default App;
